import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Lock, 
  Mail, 
  User, 
  Eye, 
  EyeOff,
  Check,
  AlertCircle,
  Fingerprint,
  Trash2,
  Smartphone,
  Sun,
  Moon,
  Type,
  Palette,
  Phone,
  Shield,
  Camera,
  MapPin,
  Flag,
  Edit,
  X,
  Circle,
  HelpCircle,
  Plus,
  ImagePlus,
  Images,
  Play
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import PhoneVerificationModal from "./PhoneVerificationModal";
import { BUBBLE_COLORS } from "./UserBubble";
import ImageCropModal from "./ImageCropModal";
import { isRonRoHelpHidden, enableRonRoHelpButton } from "./RonRoHelpButton";
import { getThemePreference } from '../utils/themePreference';
import { StartupSplash } from './overlays/StartupSplash';
import { ScoreAnimationSetting } from './score-reactions/ScoreAnimationSetting';
import { HomeAnimationSetting } from './home-animation/HomeAnimationSetting';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required');
const API = `${BACKEND_URL}/api`;

const SettingsPage = ({ currentUser, profile }) => {
  const navigate = useNavigate();
  
  // Theme state
  const [theme, setTheme] = useState(getThemePreference);
  const [showSplashPreview, setShowSplashPreview] = useState(false);
  
  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem('ps-font-size') || 'small';
  });
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Username change state
  const [showUsernameSection, setShowUsernameSection] = useState(false);
  const [newUsername, setNewUsername] = useState(profile?.username || '');
  const [usernameLoading, setUsernameLoading] = useState(false);
  
  // Biometric state
  const [showBiometricSection, setShowBiometricSection] = useState(false);
  const [biometricCredentials, setBiometricCredentials] = useState([]);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [isWebAuthnSupported, setIsWebAuthnSupported] = useState(false);

  // Phone verification state
  const [showPhoneSection, setShowPhoneSection] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState({ has_phone: false, phone_verified: false, phone_number: null });
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  
  // Edit Profile state
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    bio: '',
    location: '',
    home_course: '',
    username: '',
    looking_for: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Multi-photo upload state
  const [profilePhotos, setProfilePhotos] = useState([]);
  const [uploadingAdditionalPhoto, setUploadingAdditionalPhoto] = useState(false);
  const additionalPhotoInputRef = useRef(null);
  const [lookingForSaving, setLookingForSaving] = useState(false);
  
  // Image crop modal state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  
  // Check if profile is incomplete (for reminder)
  const isProfileIncomplete = !profile?.location && !profile?.home_course;
  
  // Bubble color state
  const [bubbleColor, setBubbleColor] = useState(profile?.bubble_color || 'green');
  const [bubbleColorLoading, setBubbleColorLoading] = useState(false);

  // Apply theme changes
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('theme-dark', 'theme-light');
    root.classList.add(`theme-${theme}`);
    localStorage.setItem('ps-theme', theme);
  }, [theme]);

  // Apply font size changes
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${fontSize}`);
    localStorage.setItem('ps-font-size', fontSize);
  }, [fontSize]);

  // Check WebAuthn support and load biometric status
  useEffect(() => {
    setIsWebAuthnSupported(window.PublicKeyCredential !== undefined);
    loadBiometricStatus();
    loadPhoneStatus();
  }, []);
  
  // Sync bubble color when profile loads
  useEffect(() => {
    if (profile?.bubble_color) {
      setBubbleColor(profile.bubble_color);
    }
  }, [profile?.bubble_color]);

  const loadPhoneStatus = async () => {
    try {
      const token = localStorage.getItem('parseeker_session_token');
      const response = await axios.get(`${API}/auth/phone-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhoneStatus(response.data);
    } catch (err) {
      console.error("Failed to load phone status:", err);
    }
  };

  const handlePhoneVerified = (phoneNumber) => {
    setShowPhoneModal(false);
    setPhoneStatus({ has_phone: true, phone_verified: true, phone_number: phoneNumber });
    toast.success("Phone number verified successfully!");
  };
  
  // Handle bubble color change
  const handleBubbleColorChange = async (colorKey) => {
    setBubbleColorLoading(true);
    try {
      await axios.put(`${API}/profile/me`, {
        username: profile?.username || '',
        bubble_color: colorKey
      });
      setBubbleColor(colorKey);
      toast.success(`Bubble color changed to ${BUBBLE_COLORS[colorKey]?.name || colorKey}!`);
    } catch (err) {
      toast.error("Failed to update bubble color");
    } finally {
      setBubbleColorLoading(false);
    }
  };
  
  // Track image loading errors
  const [avatarError, setAvatarError] = useState(false);
  
  // Track if we're just updating the photo (to avoid resetting form data)
  const [isUpdatingPhoto, setIsUpdatingPhoto] = useState(false);
  
  // Reset avatar error when profile picture changes
  useEffect(() => {
    setAvatarError(false);
  }, [profile?.picture]);
  
  // Load profile data when Edit Profile section opens (but not when just updating photo)
  useEffect(() => {
    if (showEditProfile && profile && !isUpdatingPhoto) {
      setProfileData({
        bio: profile.bio || '',
        location: profile.location || '',
        home_course: profile.home_course || '',
        username: profile.username || '',
        looking_for: profile.looking_for || ''
      });
      // Load profile photos array
      setProfilePhotos(profile.photos || []);
    }
    // Reset the flag after processing
    if (isUpdatingPhoto) {
      setIsUpdatingPhoto(false);
    }
  }, [showEditProfile, profile, isUpdatingPhoto]);
  
  const handleSaveProfile = async () => {
    setProfileLoading(true);
    try {
      await axios.put(`${API}/profile/me`, {
        bio: profileData.bio,
        location: profileData.location,
        home_course: profileData.home_course,
        username: profileData.username
      });
      toast.success("Profile updated successfully!");
      setShowEditProfile(false);
      // Reload the page to refresh profile data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };
  
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Store original file for fallback
    setOriginalFile(file);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    
    // Create a preview URL and open the crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setShowCropModal(true);
    };
    reader.onerror = () => {
      // If FileReader fails, upload without cropping
      toast.error("Could not read image. Uploading original...");
      handleDirectUpload(file);
    };
    reader.readAsDataURL(file);
    
    // Reset the file input so the same file can be selected again if needed
    e.target.value = '';
  };

  // Direct upload without cropping (fallback)
  const handleDirectUpload = async (file) => {
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/profile/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      
      toast.success("Profile photo updated!");
      setIsUpdatingPhoto(true);
      if (response.data?.picture) {
        setProfileData(prev => ({ ...prev, picture: response.data.picture }));
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.response?.data?.detail || "Failed to upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCroppedPhoto = async (croppedFile) => {
    setUploadingPhoto(true);
    setShowCropModal(false);
    try {
      const formData = new FormData();
      formData.append('file', croppedFile);
      
      const response = await axios.post(`${API}/profile/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000 // 30 second timeout for upload
      });
      
      toast.success("Profile photo updated!");
      // Set flag to prevent useEffect from resetting form data
      setIsUpdatingPhoto(true);
      // Update local profile state with new photo URL
      // This preserves any unsaved form data (Home Course, Location, etc.)
      if (response.data?.picture) {
        setProfileData(prev => ({ ...prev, picture: response.data.picture }));
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.response?.data?.detail || "Failed to upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
      setCropImageSrc(null);
      setOriginalFile(null);
    }
  };

  // Handle "Looking For" save
  const handleSaveLookingFor = async () => {
    setLookingForSaving(true);
    try {
      await axios.patch(`${API}/profile/looking-for`, {
        looking_for: profileData.looking_for
      });
      toast.success('Golf Seeking preferences saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setLookingForSaving(false);
    }
  };

  // Handle additional profile photo upload (for multi-photo gallery)
  const handleAdditionalPhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      e.target.value = '';
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      e.target.value = '';
      return;
    }
    
    // Check max photos limit
    if (profilePhotos.length >= 6) {
      toast.error("Maximum 6 photos allowed. Remove one first.");
      e.target.value = '';
      return;
    }
    
    setUploadingAdditionalPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/profile/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      
      if (response.data?.photos) {
        setProfilePhotos(response.data.photos);
        toast.success(`Photo added! (${response.data.total}/6)`);
      }
    } catch (error) {
      console.error('Additional photo upload error:', error);
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingAdditionalPhoto(false);
      e.target.value = '';
    }
  };

  // Handle removing an additional profile photo
  const handleRemoveAdditionalPhoto = async (photoIndex) => {
    try {
      const response = await axios.delete(`${API}/profile/photos/${photoIndex}`);
      if (response.data?.photos !== undefined) {
        setProfilePhotos(response.data.photos);
        toast.success("Photo removed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove photo");
    }
  };

  const loadBiometricStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/biometric/status`);
      setBiometricCredentials(response.data.credentials || []);
    } catch (err) {
      console.error("Failed to load biometric status:", err);
    }
  };

  const handleEnableBiometric = async () => {
    if (!isWebAuthnSupported) {
      toast.error("Face ID / Touch ID not supported on this device");
      return;
    }

    setBiometricLoading(true);
    try {
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: {
            name: "Par Seeker",
            id: window.location.hostname
          },
          user: {
            id: Uint8Array.from(currentUser.user_id || currentUser.email, c => c.charCodeAt(0)),
            name: currentUser.email,
            displayName: profile?.username || currentUser.name || currentUser.email
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" },
            { alg: -257, type: "public-key" }
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "required",
            residentKey: "preferred"
          },
          timeout: 60000,
          attestation: "none"
        }
      });

      if (credential) {
        const credentialData = {
          credential_id: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
          public_key: btoa(String.fromCharCode(...new Uint8Array(credential.response.getPublicKey()))),
          device_name: navigator.platform || 'Unknown Device'
        };

        await axios.post(`${API}/auth/biometric/register`, credentialData);
        toast.success("Face ID / Touch ID enabled!");
        loadBiometricStatus();
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error("Biometric authentication was cancelled");
      } else {
        console.error("Biometric setup failed:", err);
        toast.error("Failed to set up Face ID / Touch ID");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleRemoveBiometric = async (credentialId) => {
    try {
      await axios.delete(`${API}/auth/biometric/${credentialId}`);
      toast.success("Biometric credential removed");
      loadBiometricStatus();
    } catch (err) {
      toast.error("Failed to remove biometric credential");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully!');
      
      setTimeout(() => {
        setShowPasswordSection(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Current password is incorrect") {
        setPasswordError('Current password is incorrect');
      } else if (detail === "Please use Google login for this account") {
        setPasswordError('This account uses Google Sign-In. Password cannot be changed.');
      } else {
        setPasswordError(detail || 'Failed to change password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || newUsername.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    setUsernameLoading(true);
    try {
      await axios.put(`${API}/profile/me`, { username: newUsername });
      toast.success('Username updated!');
      setShowUsernameSection(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update username');
    } finally {
      setUsernameLoading(false);
    }
  };

  const isGoogleUser = currentUser?.auth_provider === 'google' || profile?.auth_provider === 'google';

  return (
    <div className="bg-[var(--ps-bg-base)] max-w-lg mx-auto animate-fade-in p-4 min-h-screen" data-testid="settings-page">
      {showSplashPreview && <StartupSplash onComplete={() => setShowSplashPreview(false)} />}
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-[var(--ps-bg-hover)] rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-[var(--ps-text-secondary)]" />
        </button>
        <h1 className="text-2xl font-black text-[var(--ps-text-primary)]">Account Settings</h1>
      </div>

      {/* User Info Card */}
      <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl mb-4 p-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            {(() => {
              const colorConfig = BUBBLE_COLORS[profile?.bubble_color] || BUBBLE_COLORS.green;
              const showInitial = !profile?.picture || avatarError;
              return (
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden"
                  style={{
                    backgroundColor: 'var(--ps-bg-surface)',
                    border: `3px solid ${colorConfig.color}`,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  {!showInitial ? (
                    <img 
                      src={profile.picture} 
                      alt={profile.username || 'User'} 
                      className="w-full h-full object-cover"
                      onError={() => setAvatarError(true)}
                    />
                  ) : (
                    <span className="text-2xl font-bold" style={{ color: colorConfig.color }}>
                      {(profile?.username || currentUser?.name || 'U')[0].toUpperCase()}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-[var(--ps-text-primary)]">
              {profile?.username || currentUser?.name || 'User'}
            </h2>
            <p className="text-[var(--ps-text-secondary)] text-sm">{currentUser?.email}</p>
            {isGoogleUser && (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--ps-info)] mt-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google Account
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Profile Incomplete Reminder */}
      {isProfileIncomplete && (
        <div 
          className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl mb-4 p-4 cursor-pointer hover:border-amber-500/50 transition-colors"
          onClick={() => setShowEditProfile(true)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-400">Complete Your Profile</p>
              <p className="text-sm text-amber-300/70">Add your home course and location to connect with other golfers</p>
            </div>
            <Edit className="w-5 h-5 text-amber-400" />
          </div>
        </div>
      )}

      {/* ==================== EDIT PROFILE SECTION ==================== */}
      <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl mb-4 overflow-hidden">
        <button
          onClick={() => setShowEditProfile(!showEditProfile)}
          className="w-full p-4 flex items-center justify-between hover:bg-[var(--ps-bg-hover)] transition-colors"
          data-testid="edit-profile-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--ps-primary)]/20 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-[var(--ps-primary)]" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-[var(--ps-text-primary)]">Edit Profile</p>
              <p className="text-sm text-[var(--ps-text-secondary)]">Update your photo & details</p>
            </div>
          </div>
          <div className={`transform transition-transform ${showEditProfile ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-[var(--ps-text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        
        {showEditProfile && (
          <div className="p-4 border-t border-[var(--ps-border)] space-y-4">
            {/* Profile Photo */}
            <div className="flex items-center gap-4">
              <div className="relative">
                {(() => {
                  const colorConfig = BUBBLE_COLORS[profile?.bubble_color] || BUBBLE_COLORS.green;
                  const showInitial = !profile?.picture || avatarError;
                  return (
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden"
                      style={{
                        backgroundColor: 'var(--ps-bg-surface)',
                        border: `3px solid ${colorConfig.color}`,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                      }}
                    >
                      {!showInitial ? (
                        <img 
                          src={profile.picture} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                          onError={() => setAvatarError(true)}
                        />
                      ) : (
                        <span className="text-3xl font-bold" style={{ color: colorConfig.color }}>
                          {(profile?.username || currentUser?.name || 'U')[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                  );
                })()}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute -bottom-1 -right-1 w-8 h-8 bg-[var(--ps-primary)] rounded-full flex items-center justify-center text-white hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {uploadingPhoto ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-[var(--ps-text-primary)]">Profile Photo</p>
                <p className="text-xs text-[var(--ps-text-muted)]">JPG, PNG or GIF. Max 5MB.</p>
              </div>
            </div>
            
            {/* Username */}
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-primary)] mb-1.5 block">Username</Label>
              <Input
                value={profileData.username}
                onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                placeholder="Enter username"
                className="bg-[var(--ps-bg-input)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
              />
            </div>
            
            {/* Home Course */}
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-primary)] mb-1.5 block">
                <Flag className="w-4 h-4 inline mr-1 text-[var(--ps-primary)]" />
                Home Course <span className="text-[var(--ps-primary)]">*</span>
              </Label>
              <Input
                value={profileData.home_course}
                onChange={(e) => setProfileData({ ...profileData, home_course: e.target.value })}
                placeholder="e.g., Pebble Beach Golf Links"
                className="bg-[var(--ps-bg-input)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
              />
            </div>
            
            {/* Location */}
            <div>
              <Label className="text-sm font-medium text-[var(--ps-text-primary)] mb-1.5 block">
                <MapPin className="w-4 h-4 inline mr-1 text-[var(--ps-primary)]" />
                Location <span className="text-[var(--ps-primary)]">*</span>
              </Label>
              <Input
                value={profileData.location}
                onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                placeholder="e.g., Detroit, Michigan"
                className="bg-[var(--ps-bg-input)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
              />
            </div>
            
            {/* ==================== LOOKING FOR SECTION (PS Members) ==================== */}
            <div className="pt-4 border-t border-[var(--ps-border)]">
              <Label htmlFor="golf-seeking-preferences" data-testid="golf-seeking-label" className="text-sm font-medium text-[var(--ps-text-primary)] mb-1.5 block">
                <span aria-hidden="true" className="mr-1">⛳</span>
                What type of golf are you Seeking
              </Label>
              <p className="text-xs text-[var(--ps-text-muted)] mb-2">
                Tournament seeker, Competition seeker, casual rounds, or new golf buddies.
              </p>
              <Textarea
                id="golf-seeking-preferences"
                value={profileData.looking_for}
                onChange={(e) => setProfileData({ ...profileData, looking_for: e.target.value })}
                placeholder="e.g., Tournament seeker, Competition seeker, weekend golf buddies, casual rounds, or exploring new courses!"
                className="bg-[var(--ps-bg-input)] border-[var(--ps-border)] text-[var(--ps-text-primary)] min-h-[100px] resize-none"
                maxLength={500}
                data-testid="looking-for-textarea"
              />
              <div className="flex items-center justify-between mt-2">
                <span data-testid="golf-seeking-character-count" className="text-xs text-[var(--ps-text-muted)]">
                  {profileData.looking_for?.length || 0}/500 characters
                </span>
                <Button
                  onClick={handleSaveLookingFor}
                  disabled={lookingForSaving}
                  size="sm"
                  className="bg-green-700 hover:bg-green-800 text-white"
                  data-testid="save-looking-for-btn"
                >
                  {lookingForSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>
            
            {/* ==================== PROFILE PHOTOS GALLERY (Multi-image upload) ==================== */}
            <div className="pt-4 border-t border-[var(--ps-border)]">
              <Label className="text-sm font-medium text-[var(--ps-text-primary)] mb-1.5 block">
                <Images className="w-4 h-4 inline mr-1 text-blue-500" />
                Profile Photos ({profilePhotos.length}/6)
              </Label>
              <p className="text-xs text-[var(--ps-text-muted)] mb-3">
                Add up to 6 photos for your PS Members profile
              </p>
              
              {/* Photo Grid */}
              <div className="grid grid-cols-3 gap-2">
                {/* Existing Photos */}
                {profilePhotos.map((photoUrl, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)]">
                    <img 
                      src={photoUrl} 
                      alt={`Photo ${index + 1}`} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23333" width="100" height="100"/><text fill="%23666" x="50" y="55" text-anchor="middle" font-size="14">Error</text></svg>';
                      }}
                    />
                    <button
                      onClick={() => handleRemoveAdditionalPhoto(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                      title="Remove photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-[var(--ps-primary)] text-white text-[10px] font-bold rounded">
                        Main
                      </span>
                    )}
                  </div>
                ))}
                
                {/* Add Photo Button (if under limit) */}
                {profilePhotos.length < 6 && (
                  <button
                    onClick={() => additionalPhotoInputRef.current?.click()}
                    disabled={uploadingAdditionalPhoto}
                    className="aspect-square rounded-lg border-2 border-dashed border-[var(--ps-border)] hover:border-blue-500 bg-[var(--ps-bg-elevated)] flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50"
                    data-testid="add-photo-btn"
                  >
                    {uploadingAdditionalPhoto ? (
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <ImagePlus className="w-6 h-6 text-blue-500" />
                        <span className="text-xs text-[var(--ps-text-muted)]">Add</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              
              <input
                ref={additionalPhotoInputRef}
                type="file"
                accept="image/*"
                onChange={handleAdditionalPhotoSelect}
                className="hidden"
              />
            </div>
            
            {/* Save Button */}
            <Button
              onClick={handleSaveProfile}
              disabled={profileLoading}
              className="w-full bg-[var(--ps-primary)] hover:bg-green-600 text-white"
              data-testid="save-profile-btn"
            >
              {profileLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* ==================== APPEARANCE SECTION ==================== */}
      <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl mb-4 overflow-hidden">
        <div className="p-4 border-b border-[var(--ps-border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Palette className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="font-semibold text-[var(--ps-text-primary)]">Appearance</p>
              <p className="text-sm text-[var(--ps-text-secondary)]">Customize your experience</p>
            </div>
          </div>
        </div>

        {/* Theme Selection */}
        <div className="p-4 border-b border-[var(--ps-border)]">
          <div className="flex items-center gap-2 mb-3">
            {theme === 'dark' ? (
              <Moon className="w-4 h-4 text-[var(--ps-text-muted)]" />
            ) : (
              <Sun className="w-4 h-4 text-[var(--ps-text-muted)]" />
            )}
            <Label className="text-sm font-medium text-[var(--ps-text-primary)]">Theme</Label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setTheme('dark')}
              className={`p-4 rounded-xl border-2 transition-all ${
                theme === 'dark'
                  ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                  : 'border-[var(--ps-border)] hover:border-[var(--ps-border-light)]'
              }`}
              data-testid="theme-dark-btn"
              aria-pressed={theme === 'dark'}
            >
              <div className="ps-fixed-contrast w-full h-12 rounded-lg bg-[#0A0A0A] border border-[#262626] mb-2 flex items-center justify-center">
                <Moon className="w-5 h-5 text-white" />
              </div>
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
                Dark
              </p>
              {theme === 'dark' && (
                <Check className="w-4 h-4 text-[var(--ps-primary)] mx-auto mt-1" />
              )}
            </button>
            <button
              onClick={() => setTheme('light')}
              className={`p-4 rounded-xl border-2 transition-all ${
                theme === 'light'
                  ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                  : 'border-[var(--ps-border)] hover:border-[var(--ps-border-light)]'
              }`}
              data-testid="theme-light-btn"
              aria-pressed={theme === 'light'}
            >
              <div className="w-full h-12 rounded-lg bg-[#F5F5F0] border border-[#E5E5E0] mb-2 flex items-center justify-center">
                <Sun className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
                Light
              </p>
              {theme === 'light' && (
                <Check className="w-4 h-4 text-[var(--ps-primary)] mx-auto mt-1" />
              )}
            </button>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-[var(--ps-border)]">
          <Button type="button" variant="outline" className="gap-2" onClick={() => setShowSplashPreview(true)} data-testid="preview-startup-animation">
            <Play className="h-4 w-4" /> Preview startup animation
          </Button>
        </div>

        <ScoreAnimationSetting key={currentUser?.user_id} userId={currentUser?.user_id} />
        <HomeAnimationSetting key={`home-${currentUser?.user_id}`} userId={currentUser?.user_id} />

        {/* Font Size Selection */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Type className="w-4 h-4 text-[var(--ps-text-muted)]" />
            <Label className="text-sm font-medium text-[var(--ps-text-primary)]">Font Size</Label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setFontSize('small')}
              className={`p-3 rounded-xl border-2 transition-all ${
                fontSize === 'small'
                  ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                  : 'border-[var(--ps-border)] hover:border-[var(--ps-border-light)]'
              }`}
              data-testid="font-small-btn"
            >
              <p className="text-xs text-center text-[var(--ps-text-secondary)] mb-1">Aa</p>
              <p className={`text-xs font-medium text-center ${fontSize === 'small' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
                Small
              </p>
              {fontSize === 'small' && (
                <Check className="w-3 h-3 text-[var(--ps-primary)] mx-auto mt-1" />
              )}
            </button>
            <button
              onClick={() => setFontSize('medium')}
              className={`p-3 rounded-xl border-2 transition-all ${
                fontSize === 'medium'
                  ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                  : 'border-[var(--ps-border)] hover:border-[var(--ps-border-light)]'
              }`}
              data-testid="font-medium-btn"
            >
              <p className="text-sm text-center text-[var(--ps-text-secondary)] mb-1">Aa</p>
              <p className={`text-xs font-medium text-center ${fontSize === 'medium' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
                Medium
              </p>
              {fontSize === 'medium' && (
                <Check className="w-3 h-3 text-[var(--ps-primary)] mx-auto mt-1" />
              )}
            </button>
            <button
              onClick={() => setFontSize('large')}
              className={`p-3 rounded-xl border-2 transition-all ${
                fontSize === 'large'
                  ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                  : 'border-[var(--ps-border)] hover:border-[var(--ps-border-light)]'
              }`}
              data-testid="font-large-btn"
            >
              <p className="text-base text-center text-[var(--ps-text-secondary)] mb-1">Aa</p>
              <p className={`text-xs font-medium text-center ${fontSize === 'large' ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
                Large
              </p>
              {fontSize === 'large' && (
                <Check className="w-3 h-3 text-[var(--ps-primary)] mx-auto mt-1" />
              )}
            </button>
          </div>
          <p className="text-xs text-[var(--ps-text-muted)] mt-3 text-center">
            Current: {fontSize.charAt(0).toUpperCase() + fontSize.slice(1)} • Changes apply instantly
          </p>
        </div>
        
        {/* Bubble Color Selection */}
        <div className="p-4 border-t border-[var(--ps-border)]">
          <div className="flex items-center gap-2 mb-3">
            <Circle className="w-4 h-4 text-[var(--ps-text-muted)]" fill="currentColor" />
            <Label className="text-sm font-medium text-[var(--ps-text-primary)]">Bubble Border Color</Label>
          </div>
          <p className="text-xs text-[var(--ps-text-muted)] mb-3">
            Your profile bubble will glow this color when you're online
          </p>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(BUBBLE_COLORS).map(([key, config]) => (
              <button
                key={key}
                onClick={() => handleBubbleColorChange(key)}
                disabled={bubbleColorLoading}
                className={`relative p-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                  bubbleColor === key
                    ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10'
                    : 'border-[var(--ps-border)] hover:border-[var(--ps-border-light)]'
                }`}
                data-testid={`bubble-color-${key}`}
              >
                <div 
                  className="w-8 h-8 rounded-full"
                  style={{ 
                    backgroundColor: config.color,
                    boxShadow: bubbleColor === key ? config.glow : 'none'
                  }}
                />
                <p className={`text-[10px] font-medium ${bubbleColor === key ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
                  {config.name}
                </p>
                {bubbleColor === key && (
                  <Check className="w-3 h-3 text-[var(--ps-primary)] absolute top-1 right-1" />
                )}
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--ps-text-muted)] mt-3 text-center">
            Current: {BUBBLE_COLORS[bubbleColor]?.name || 'Green'} • {bubbleColorLoading ? 'Saving...' : 'Tap to change'}
          </p>
        </div>
        
        {/* RonRo Help Button Toggle */}
        {isRonRoHelpHidden() && (
          <div className="p-4 border-t border-[var(--ps-border)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-[var(--ps-text-primary)]">RonRo Help Button</p>
                  <p className="text-xs text-[var(--ps-text-muted)]">Floating assistant button</p>
                </div>
              </div>
              <Button
                onClick={enableRonRoHelpButton}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-3"
                data-testid="enable-ronro-btn"
              >
                Show
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Username Section */}
      <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl mb-4 overflow-hidden">
        <button
          onClick={() => setShowUsernameSection(!showUsernameSection)}
          className="w-full p-4 flex items-center justify-between hover:bg-[var(--ps-bg-hover)] transition-colors"
          data-testid="username-section-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--ps-info)]/20 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-[var(--ps-info)]" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-[var(--ps-text-primary)]">Username</p>
              <p className="text-sm text-[var(--ps-text-secondary)]">{profile?.username || 'Not set'}</p>
            </div>
          </div>
          <span className="text-[var(--ps-text-muted)]">Edit</span>
        </button>

        {showUsernameSection && (
          <div className="px-4 pb-4 pt-2 border-t border-[var(--ps-border)]">
            <form onSubmit={handleChangeUsername} className="space-y-4">
              <div>
                <Label className="text-[var(--ps-text-secondary)] text-sm">New Username</Label>
                <Input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="mt-1 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                  placeholder="Enter new username"
                  data-testid="new-username-input"
                />
              </div>
              <Button 
                type="submit" 
                className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-hover)] text-white w-full"
                disabled={usernameLoading}
              >
                {usernameLoading ? (
                  <div className="dark-spinner w-5 h-5" />
                ) : (
                  'Update Username'
                )}
              </Button>
            </form>
          </div>
        )}
      </div>

      {/* Phone Number Section */}
      <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl mb-4 overflow-hidden">
        <button
          onClick={() => setShowPhoneSection(!showPhoneSection)}
          className="w-full p-4 flex items-center justify-between hover:bg-[var(--ps-bg-hover)] transition-colors"
          data-testid="phone-section-btn"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${phoneStatus.phone_verified ? 'bg-[var(--ps-success)]/20' : 'bg-[var(--ps-warning)]/20'} rounded-lg flex items-center justify-center`}>
              <Phone className={`w-5 h-5 ${phoneStatus.phone_verified ? 'text-[var(--ps-success)]' : 'text-[var(--ps-warning)]'}`} />
            </div>
            <div className="text-left">
              <p className="font-semibold text-[var(--ps-text-primary)]">Phone Number</p>
              <p className="text-sm text-[var(--ps-text-secondary)]">
                {phoneStatus.phone_verified 
                  ? `Verified: ${phoneStatus.phone_number}` 
                  : 'Not verified'}
              </p>
            </div>
          </div>
          <span className="text-[var(--ps-text-muted)]">
            {phoneStatus.phone_verified ? 'Change' : 'Add'}
          </span>
        </button>

        {showPhoneSection && (
          <div className="px-4 pb-4 pt-2 border-t border-[var(--ps-border)]">
            {phoneStatus.phone_verified ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-[var(--ps-success)]/10 rounded-lg">
                  <Shield className="w-5 h-5 text-[var(--ps-success)]" />
                  <div>
                    <p className="text-sm font-medium text-[var(--ps-text-primary)]">Phone Verified</p>
                    <p className="text-xs text-[var(--ps-text-secondary)]">{phoneStatus.phone_number}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowPhoneModal(true)}
                  variant="outline"
                  className="w-full"
                  data-testid="change-phone-btn"
                >
                  Change Phone Number
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[var(--ps-text-secondary)]">
                  Add a verified phone number for account security and recovery.
                </p>
                <Button 
                  onClick={() => setShowPhoneModal(true)}
                  className="w-full dark-btn-primary"
                  data-testid="add-phone-settings-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Add Phone Number
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Biometric Section */}
      {isWebAuthnSupported && (
        <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl mb-4 overflow-hidden">
          <button
            onClick={() => setShowBiometricSection(!showBiometricSection)}
            className="w-full p-4 flex items-center justify-between hover:bg-[var(--ps-bg-hover)] transition-colors"
            data-testid="biometric-section-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[var(--ps-success)]/20 rounded-lg flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-[var(--ps-success)]" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-[var(--ps-text-primary)]">Face ID / Touch ID</p>
                <p className="text-sm text-[var(--ps-text-secondary)]">
                  {biometricCredentials.length > 0 
                    ? `${biometricCredentials.length} device${biometricCredentials.length > 1 ? 's' : ''} enrolled` 
                    : 'Not set up'}
                </p>
              </div>
            </div>
            <span className="text-[var(--ps-text-muted)]">
              {biometricCredentials.length > 0 ? 'Manage' : 'Set up'}
            </span>
          </button>

          {showBiometricSection && (
            <div className="px-4 pb-4 pt-2 border-t border-[var(--ps-border)]">
              {biometricCredentials.length > 0 && (
                <div className="mb-4 space-y-2">
                  {biometricCredentials.map((cred, index) => (
                    <div 
                      key={cred.credential_id || index}
                      className="flex items-center justify-between p-3 bg-[var(--ps-bg-elevated)] rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-[var(--ps-text-muted)]" />
                        <span className="text-sm text-[var(--ps-text-primary)]">
                          {cred.device_name || `Device ${index + 1}`}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveBiometric(cred.credential_id)}
                        className="p-1 text-[var(--ps-error)] hover:bg-[var(--ps-error)]/10 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Button 
                onClick={handleEnableBiometric}
                className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-hover)] text-white w-full"
                disabled={biometricLoading}
              >
                {biometricLoading ? (
                  <div className="dark-spinner w-5 h-5" />
                ) : (
                  <>
                    <Fingerprint className="w-4 h-4 mr-2" />
                    {biometricCredentials.length > 0 ? 'Add Another Device' : 'Enable Face ID / Touch ID'}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Password Section */}
      {!isGoogleUser && (
        <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-2xl mb-4 overflow-hidden">
          <button
            onClick={() => setShowPasswordSection(!showPasswordSection)}
            className="w-full p-4 flex items-center justify-between hover:bg-[var(--ps-bg-hover)] transition-colors"
            data-testid="password-section-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[var(--ps-warning)]/20 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-[var(--ps-warning)]" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-[var(--ps-text-primary)]">Password</p>
                <p className="text-sm text-[var(--ps-text-secondary)]">Change your password</p>
              </div>
            </div>
            <span className="text-[var(--ps-text-muted)]">Change</span>
          </button>

          {showPasswordSection && (
            <div className="px-4 pb-4 pt-2 border-t border-[var(--ps-border)]">
              {passwordError && (
                <div className="flex items-center gap-2 p-3 bg-[var(--ps-error)]/10 text-[var(--ps-error)] rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{passwordError}</span>
                </div>
              )}
              
              {passwordSuccess && (
                <div className="flex items-center gap-2 p-3 bg-[var(--ps-success)]/10 text-[var(--ps-success)] rounded-lg mb-4">
                  <Check className="w-4 h-4" />
                  <span className="text-sm">Password changed successfully!</span>
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label className="text-[var(--ps-text-secondary)] text-sm">Current Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)] pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ps-text-muted)]"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-[var(--ps-text-secondary)] text-sm">New Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)] pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ps-text-muted)]"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-[var(--ps-text-secondary)] text-sm">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-hover)] text-white w-full"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? (
                    <div className="dark-spinner w-5 h-5" />
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Forgot Password Link */}
      {!isGoogleUser && (
        <div className="text-center">
          <button
            onClick={() => navigate('/?forgot=true')}
            className="text-sm text-[var(--ps-info)] hover:text-[var(--ps-primary)]"
            data-testid="forgot-password-link"
          >
            Forgot your password? Reset via email
          </button>
        </div>
      )}

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onVerified={handlePhoneVerified}
        email={currentUser?.email || profile?.email || ''}
      />

      {/* Image Crop Modal */}
      <ImageCropModal
        isOpen={showCropModal}
        onClose={() => {
          setShowCropModal(false);
          setCropImageSrc(null);
          setOriginalFile(null);
        }}
        imageSrc={cropImageSrc}
        onCropComplete={handleCroppedPhoto}
        onSkip={() => {
          setShowCropModal(false);
          setCropImageSrc(null);
          if (originalFile) {
            handleDirectUpload(originalFile);
          }
        }}
      />
    </div>
  );
};

export default SettingsPage;
